import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMyEstablishments } from '../services/assujettiService';

interface License {
  id: string;
  establishment_name: string;
  activity: string;
  category: string;
  star_rating?: number;
  fork_rating?: number;
  license_number: string;
  start_date: string;
  duration_months: number;
  expiry_date: string;
  amount: number;
  currency: string;
  status: string;
}

function daysRemaining(expiry: string): number {
  return Math.ceil((new Date(expiry).getTime() - Date.now()) / 86400000);
}

function daysClass(d: number) {
  if (d <= 0) return 'text-red-600 font-semibold';
  if (d <= 90) return 'text-orange-600 font-semibold';
  return 'text-green-600';
}

function statusBadge(status: string) {
  const s = status?.toLowerCase() || '';
  if (s.includes('valid')) return <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">Valide</span>;
  if (s.includes('renouvel') || s === 'expiring_soon') return <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">A renouveler</span>;
  return <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Expiree</span>;
}

function ratingDisplay(row: License) {
  if (row.star_rating) return '\u2605'.repeat(row.star_rating) + ' ' + row.category;
  if (row.fork_rating) return '\uD83C\uDF74'.repeat(row.fork_rating) + ' ' + row.category;
  return row.category;
}

export default function V4LicensesPage() {
  const [rows, setRows] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getMyEstablishments()
      .then((r: any) => {
        const list = Array.isArray(r) ? r : r.data ?? r.establishments ?? [];
        setRows(list.map((e: any) => ({
          id: e.id,
          establishment_name: e.name || e.establishment_name,
          activity: e.activity,
          category: e.category,
          star_rating: e.star_rating,
          fork_rating: e.fork_rating,
          license_number: e.license_number,
          start_date: e.start_date || e.license_start,
          duration_months: e.duration_months,
          expiry_date: e.expiry_date || e.license_expiry,
          amount: e.renewal_amount ?? e.amount ?? 0,
          currency: e.currency || 'USD',
          status: e.status,
        })));
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="max-w-7xl mx-auto py-10 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" /><p className="mt-4 text-gray-500">Chargement...</p></div>;
  if (error) return <div className="max-w-7xl mx-auto py-10"><div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div></div>;

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Licences</h1>
          <p className="text-gray-500 text-sm">Categorie, duree de validite, dates et montant de renouvellement.</p>
        </div>
        <Link to="/fpt/v4/renewals" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">Preparer un renouvellement</Link>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">Aucune licence trouvee.</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Etablissement', 'Categorie', 'N. licence', 'Debut validite', 'Duree categorie', 'Date renouvellement', 'Jours restants', 'Montant', 'Statut'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map(r => {
                const days = r.expiry_date ? daysRemaining(r.expiry_date) : 0;
                return (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{r.establishment_name}</td>
                    <td className="px-4 py-4 text-sm">{ratingDisplay(r)}</td>
                    <td className="px-4 py-4 text-sm font-mono text-gray-600">{r.license_number || '-'}</td>
                    <td className="px-4 py-4 text-sm">{r.start_date ? new Date(r.start_date).toLocaleDateString('fr-FR') : '-'}</td>
                    <td className="px-4 py-4 text-sm font-semibold">{r.duration_months ? `${r.duration_months} mois` : '-'}</td>
                    <td className="px-4 py-4 text-sm">{r.expiry_date ? new Date(r.expiry_date).toLocaleDateString('fr-FR') : '-'}</td>
                    <td className={`px-4 py-4 text-sm ${daysClass(days)}`}>{days}</td>
                    <td className="px-4 py-4 text-sm">{r.amount ? `${r.amount.toLocaleString()} ${r.currency}` : '-'}</td>
                    <td className="px-4 py-4">{statusBadge(r.status)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
